import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import HlsVideo from "@/components/HlsVideo";
import { resolveMedia } from "@/lib/media";

function fmtTime(secs) {
  if (!secs || secs < 0) return "0:00";
  const s = Math.floor(secs);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = s % 60;
  if (h > 0) return `${h}:${m.toString().padStart(2, "0")}:${r.toString().padStart(2, "0")}`;
  return `${m}:${r.toString().padStart(2, "0")}`;
}

/**
 * ContinueWatching — long-form resume-watching rail.
 *
 * Renders a horizontal scroller of the current user's in-progress videos.
 * Tap a card to jump to /watch/{postId} (or wherever the router opens the
 * dedicated player) — the player picks up from `position_seconds`.
 */
export default function ContinueWatching({ limit = 12 }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/watch/continue?limit=${limit}`);
        if (!cancelled) setItems(data.items || []);
      } catch { /* silent */ }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [limit]);

  if (loading || items.length === 0) return null;

  return (
    <section className="w-full px-4 py-4" data-testid="continue-watching-rail">
      <h3 className="text-white font-heading text-lg font-black mb-3">Continue watching</h3>
      <div className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4 snap-x">
        {items.map((it) => {
          const pct = Math.max(0.02, Math.min(1, it.completion_pct || 0));
          const p = it.post || {};
          return (
            <button
              key={it.post_id}
              data-testid={`continue-card-${it.post_id}`}
              onClick={() => navigate(`/w/${it.post_id}`)}
              className="relative flex-shrink-0 snap-start rounded-xl overflow-hidden bg-white/5 ring-1 ring-white/10 hover:ring-white/30 transition text-left"
              style={{ width: 260 }}
            >
              <div className="relative w-full" style={{ aspectRatio: 16 / 9 }}>
                {p.thumbnail_url ? (
                  <img
                    src={resolveMedia(p.thumbnail_url)}
                    alt=""
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-slate-800 to-black" />
                )}
                {/* Duration chip */}
                {p.duration_seconds ? (
                  <div className="absolute bottom-2 right-2 bg-black/80 text-white text-[11px] font-heading font-bold px-1.5 py-0.5 rounded">
                    {fmtTime(p.duration_seconds)}
                  </div>
                ) : null}
                {/* Progress bar */}
                <div className="absolute inset-x-0 bottom-0 h-1 bg-white/20">
                  <div
                    className="h-full bg-red-500"
                    style={{ width: `${pct * 100}%` }}
                  />
                </div>
              </div>
              <div className="p-2">
                <p className="text-white text-sm font-medium line-clamp-2">
                  {p.caption || "Untitled"}
                </p>
                <p className="text-white/50 text-xs mt-0.5">
                  {p.author?.name || "Unknown"} · {fmtTime(it.position_seconds)} watched
                </p>
              </div>
            </button>
          );
        })}
      </div>
    </section>
  );
}

/**
 * Full-screen long-form watch page. Route: /w/:postId
 * Resumes from stored watch_progress position.
 */
export function WatchPage() {
  return null; // implemented by the page-level component in pages/Watch.jsx
}
